import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import VehicleCard from "../components/VehicleCard";
import Seo from "../components/Seo";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, ShieldCheck, Ship, Handshake, Anchor, Building2, Megaphone } from "lucide-react";

function ModuleShell({ title, subtitle, type, eyebrow, bg, testid, extraCtaTo, extraCtaText, seoTitle, seoDescription }) {
  const { lang, t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get(`/vehicles?type=${type}&limit=24`)
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, [type]);

  return (
    <div data-testid={testid}>
      {seoTitle && <Seo title={seoTitle} description={seoDescription || subtitle} />}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={bg} alt="" className="w-full h-full object-cover" />
          {/* Strong dark scrim so the title and subtitle are always readable
              regardless of the background image's brightness. */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(115deg, rgba(10,10,10,0.92) 0%, rgba(10,10,10,0.75) 55%, rgba(10,10,10,0.55) 100%)",
            }}
          />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
          <div
            className="overline mb-3"
            style={{ color: "#d4ae6a" }}
          >
            {eyebrow}
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium max-w-3xl leading-[1.05]"
            style={{ color: "#ffffff", textShadow: "0 2px 18px rgba(0,0,0,0.5)" }}
          >
            {title}
          </h1>
          <p
            className="mt-4 max-w-2xl text-base sm:text-lg leading-relaxed"
            style={{ color: "#e8e1cf" }}
          >
            {subtitle}
          </p>
          {extraCtaTo && (
            <Link to={extraCtaTo} className="btn-primary mt-6 inline-flex">
              {extraCtaText} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </section>
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="surface h-72 animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="surface rounded-2xl p-12 text-center text-[#6b6b6b]">{t("common.empty")}</div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-5 [&>*]:break-inside-avoid [&>*]:mb-5">
            {items.map((v) => <VehicleCard key={v.id} v={v} />)}
          </div>
        )}
      </section>
    </div>
  );
}

export function RentalsPage() {
  const { lang } = useI18n();
  return (
    <ModuleShell
      testid="rentals-page"
      eyebrow="RENTAL MARKETPLACE"
      type="rental"
      title={lang === "ar" ? "استأجر مركبتك. يومياً، أسبوعياً، شهرياً، سنوياً." : "Rent any vehicle. Daily, weekly, monthly, yearly."}
      subtitle={lang === "ar" ? "Budget، Lumi، Yelo، Avis، Enterprise وأكثر — في مكان واحد." : "Budget, Lumi, Yelo, Avis, Enterprise and more — in one place."}
      seoTitle={lang === "ar" ? "تأجير سيارات في السعودية | كارتال" : "Car Rentals in Saudi Arabia | CARTAL"}
      seoDescription={lang === "ar" ? "استأجر سيارة في السعودية — يومي، أسبوعي، شهري، سنوي. مقارنة Budget وLumi وYelo وAvis في مكان واحد عبر منصة كارتال." : "Rent a vehicle in Saudi Arabia — daily, weekly, monthly or yearly. Compare Budget, Lumi, Yelo, Avis and more on CARTAL."}
      bg="https://images.unsplash.com/photo-1568844293986-8d0400bd4745?w=1600&q=80"
    />
  );
}

export function SalvagePage() {
  const { lang } = useI18n();
  return (
    <ModuleShell
      testid="salvage-page"
      eyebrow="SALVAGE VEHICLE MARKETPLACE"
      type="salvage"
      title={lang === "ar" ? "مركبات السكراب والحوادث" : "Salvage, damaged & accident vehicles"}
      subtitle={lang === "ar" ? "تصنيف الأضرار، تقارير الفحص، ودعم التصدير." : "Damage classification, inspection reports and export-ready logistics."}
      seoTitle={lang === "ar" ? "سيارات تشليح وسكراب في السعودية | كارتال" : "Salvage & Scrap Vehicles in Saudi Arabia | CARTAL"}
      seoDescription={lang === "ar" ? "سوق مركبات السكراب والحوادث في السعودية — تصنيف الأضرار وتقارير فحص ودعم تصدير عبر منصة كارتال." : "Saudi Arabia's salvage and accident vehicle marketplace — damage classification, inspection reports and export logistics on CARTAL."}
      bg="https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1600&q=80"
    />
  );
}

export function ExportPage() {
  const { lang } = useI18n();
  return (
    <ModuleShell
      testid="export-page"
      eyebrow="VEHICLE EXPORT MARKETPLACE"
      type="export"
      title={lang === "ar" ? "صدّر مركبتك دولياً" : "Export vehicles internationally"}
      subtitle={lang === "ar" ? "مصدّرون مرخصون، عروض شحن، ودعم وثائق جمركية." : "Licensed exporters, shipping quotations and customs documentation."}
      seoTitle={lang === "ar" ? "تصدير السيارات من السعودية | كارتال" : "Vehicle Export from Saudi Arabia | CARTAL"}
      seoDescription={lang === "ar" ? "صدّر مركبتك دولياً عبر مصدّرين مرخصين — عروض شحن ووثائق جمركية على منصة كارتال." : "Export vehicles internationally through licensed exporters — shipping quotes and customs documentation on CARTAL."}
      bg="https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=1600&q=80"
    />
  );
}

export function HeavyPage() {
  const { lang } = useI18n();
  return (
    <ModuleShell
      testid="heavy-page"
      eyebrow="TRUCKS & HEAVY EQUIPMENT"
      type="heavy"
      title={lang === "ar" ? "المعدات الثقيلة" : "Heavy equipment"}
      subtitle={lang === "ar" ? "شاحنات، حفارات، روافع، معدات بناء — بيعاً وتأجيراً وتمويلاً." : "Trucks, excavators, cranes, construction & agricultural equipment — buy, rent, finance."}
      seoTitle={lang === "ar" ? "المعدات الثقيلة للبيع في السعودية | كارتال" : "Heavy Equipment for Sale in Saudi Arabia | CARTAL"}
      seoDescription={lang === "ar" ? "شاحنات وحفارات وروافع ومعدات بناء وزراعة للبيع والتأجير والتمويل في السعودية — عبر منصة كارتال." : "Trucks, excavators, cranes and construction equipment for sale, rent and finance across Saudi Arabia on CARTAL."}
      bg="https://images.unsplash.com/photo-1581094271901-8022df4466f9?w=1600&q=80"
    />
  );
}

export function MotorcyclesPage() {
  const { lang } = useI18n();
  return (
    <ModuleShell
      testid="motorcycles-page"
      eyebrow="MOTORCYCLE MARKETPLACE"
      type="motorcycle"
      title={lang === "ar" ? "الدراجات النارية" : "Motorcycles & scooters"}
      subtitle={lang === "ar" ? "رياضية، طوافة، كهربائية، طرق وعرة، ATV — مع تأمين وتمويل وتوصيل." : "Sports, cruiser, electric, off-road & ATV — with insurance, financing and delivery."}
      seoTitle={lang === "ar" ? "دراجات نارية للبيع في السعودية | كارتال" : "Motorcycles for Sale in Saudi Arabia | CARTAL"}
      seoDescription={lang === "ar" ? "دراجات نارية رياضية وطوافة وكهربائية وطرق وعرة وATV للبيع في السعودية مع تأمين وتمويل وتوصيل — على كارتال." : "Sports, cruiser, electric, off-road and ATV motorcycles for sale in Saudi Arabia with insurance, financing and delivery on CARTAL."}
      bg="https://images.unsplash.com/photo-1558981403-c5f9899a28bc?w=1600&q=80"
    />
  );
}

export function MarinePage() {
  const { lang, t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  // iter320 — Marine section is an ADVERTISEMENT surface for
  // yachts / boats / jet-skis. It intentionally does NOT list
  // "marine partners" any more, because doing so would link into
  // `/company/{slug}` (the Service Provider Profile) which is
  // reserved for the Service Providers Portal. Hero + CTAs are
  // enough to funnel users into `/sell?type=boat` (ads) and
  // `/join?intent=partner&activity=marine` (business onboarding),
  // both of which stay unchanged.

  // iter322 — Marine listings feed.
  // Fixes the visibility gap where a published boat (e.g. CAR-00000062)
  // was reachable via its notification but did NOT appear inside the
  // marine section, because `MarinePage` — unlike every other module
  // page (Rental / Salvage / Heavy / Motorcycle / Export) — had no
  // listings section wired up at all.
  // We reuse the same generic `GET /api/vehicles?type={type}&limit=24`
  // endpoint that already backs those other sections (iter314 kept
  // `type=boat` explicitly returnable while excluding boats from the
  // generic car feed). No new API, no hardcode, no listing patch.
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiClient
      .get("/vehicles?type=boat&limit=24")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  // iter316 — CTA router for "أضف إعلانك":
  //   • Guest → /join (partner activity carried through so the
  //     chooser + registration steer them into the marine track).
  //   • Signed-in → /sell (existing listing creation flow with the
  //     type-boat scaffolding that iter313 wired up).
  const onAddListing = (e) => {
    e.preventDefault();
    if (user) navigate("/sell?type=boat");
    else navigate("/join?intent=partner&activity=marine&next=/sell");
  };

  return (
    <div data-testid="marine-page">
      <Seo
        title={lang === "ar" ? "قوارب ويخوت للبيع في السعودية | كارتال" : "Boats & Yachts for Sale in Saudi Arabia | CARTAL"}
        description={lang === "ar" ? "قوارب ويخوت وجيت سكي للبيع في السعودية — إعلانات موثّقة ودعم فحص وتوصيل بحري على منصة كارتال." : "Boats, yachts and jet-skis for sale in Saudi Arabia — verified listings with inspection and marine delivery support on CARTAL."}
      />
      {/* Hero — untouched */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=1600&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(115deg, rgba(10,10,10,0.92) 0%, rgba(10,10,10,0.75) 55%, rgba(10,10,10,0.55) 100%)",
            }}
          />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
          <div className="overline mb-3" style={{ color: "#d4ae6a" }}>
            MARINE MARKETPLACE
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium max-w-3xl leading-[1.05]"
            style={{ color: "#ffffff", textShadow: "0 2px 18px rgba(0,0,0,0.5)" }}
          >
            {lang === "ar" ? "اليخوت والزوارق والجت سكي" : "Yachts, boats & jet skis"}
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg leading-relaxed" style={{ color: "#e8e1cf" }}>
            {lang === "ar"
              ? "يخوت فاخرة، زوارق صيد، جت سكي، ومعدات بحرية — مع تمويل وتأمين ونقل بحري."
              : "Luxury yachts, fishing boats, jet skis and marine equipment — with marine financing, insurance and shipping."}
          </p>
        </div>
      </section>

      {/* iter320 — Marine partners grid REMOVED.
          The previous grid fetched `/platform/public/companies?category=marine`
          and linked each card to `/company/{slug}` — the unified Service
          Provider Profile. Marine is an ADVERTISEMENT section, not a
          portal, so those links were leaking the Service Provider
          Profile outside the Service Providers Portal.
          Nothing else here changes: the hero above and the two action
          CTAs below (`/join?intent=partner&activity=marine` and
          `/sell?type=boat`) do NOT touch `/company/{slug}` and stay as
          they were. Boat/yacht advertisements keep working. */}

      {/* iter322 — Marine listings feed. Reuses `VehicleCard`
          and the same responsive columns pattern that the other
          module pages use through `ModuleShell`, so a published
          boat (CAR-000000xx) shows up here automatically without
          any per-listing patch. */}
      <section
        className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12"
        data-testid="marine-listings-section"
      >
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="surface h-72 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div
            className="surface rounded-2xl p-12 text-center text-[#6b6b6b]"
            data-testid="marine-listings-empty"
          >
            {t("common.empty")}
          </div>
        ) : (
          <div
            className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-5 [&>*]:break-inside-avoid [&>*]:mb-5"
            data-testid="marine-listings-grid"
          >
            {items.map((v) => (
              <VehicleCard key={v.id} v={v} />
            ))}
          </div>
        )}
      </section>

      {/* iter316 — Marine action strip. Two premium CTAs, gold-on-dark
          identity, RTL-safe, mobile-first. Uses existing routes only
          (`/join` for onboarding, `/sell` for the listing form). No
          new backend, no new schema. */}
      <section
        className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10 pb-2"
        data-testid="marine-action-strip"
      >
        {/* iter321 — "Add your marine business" CTA removed per owner
            request. Single-CTA layout, full-width, keeps the existing
            visual identity of the listing card intact. */}

        {/* CTA — Add a sale/rental listing */}
        <div
          className="relative overflow-hidden rounded-3xl p-6 sm:p-7"
          style={{
            background:
              "linear-gradient(140deg,#04121a 0%,#062035 55%,#083049 100%)",
            border: "1px solid rgba(14,116,144,0.55)",
            boxShadow: "0 20px 50px -20px rgba(14,116,144,0.55)",
          }}
          data-testid="marine-cta-add-listing"
        >
          <div
            className="absolute -top-16 -end-16 w-56 h-56 rounded-full blur-3xl opacity-25 pointer-events-none"
            style={{ background: "#0e7490" }}
            aria-hidden
          />
          <div className="relative">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
              style={{
                background: "linear-gradient(135deg,#22d3ee,#0e7490)",
                boxShadow: "0 10px 25px -8px rgba(14,116,144,0.65)",
              }}
            >
              <Megaphone className="w-5 h-5 text-white" strokeWidth={2.4} />
            </div>
            <div className="text-[10px] tracking-[0.28em] font-black uppercase mb-1" style={{ color: "#67e8f9" }}>
              {lang === "ar" ? "إعلاناتك البحرية" : "MARINE LISTINGS"}
            </div>
            <h3 className="font-display text-xl sm:text-2xl font-black leading-snug text-[#eaf7fc]">
              {lang === "ar" ? "أضف إعلانك إلى عالم البحر" : "Add your listing to the world of the sea"}
            </h3>
            <p className="mt-2 text-sm text-[#eaf7fc]/75 leading-relaxed max-w-md">
              {lang === "ar"
                ? "بع أو أجّر، وقدّم رحلات الصيد والسياحة البحرية والاستعراضات البحرية لجمهور كارتال."
                : "Sell or rent, and offer fishing trips, marine tourism and marine shows to the CARTAL audience."}
            </p>
            <a
              href="/sell?type=boat"
              onClick={onAddListing}
              data-testid="marine-cta-add-listing-btn"
              className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-transform hover:scale-[1.03]"
              style={{
                background: "linear-gradient(135deg,#22d3ee,#0e7490)",
                color: "#ffffff",
              }}
            >
              {lang === "ar" ? "أضف إعلانك" : "Post your listing"}
              <ArrowRight className="w-4 h-4 rtl:rotate-180" strokeWidth={2.6} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export function FleetPage() {
  const { lang } = useI18n();
  const services = [
    [lang === "ar" ? "تأجير الأساطيل" : "Fleet leasing", lang === "ar" ? "عقود مرنة 1-5 سنوات" : "Flexible 1-5 year contracts"],
    [lang === "ar" ? "استبدال الأسطول" : "Fleet replacement", lang === "ar" ? "تحديث دوري بدون توقف" : "Periodic refresh with zero downtime"],
    [lang === "ar" ? "تأجير طويل المدى" : "Long-term rental", lang === "ar" ? "تأجير سنوي بأسعار تفضيلية" : "Annual rental at preferred rates"],
    [lang === "ar" ? "إدارة الأسطول" : "Fleet management", lang === "ar" ? "تتبع، تقارير، صيانة استباقية" : "Tracking, reporting, proactive servicing"],
  ];
  return (
    <div data-testid="fleet-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/17744273/pexels-photo-17744273.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1400" alt="" className="w-full h-full object-cover" />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(115deg, rgba(10,10,10,0.92) 0%, rgba(10,10,10,0.75) 55%, rgba(10,10,10,0.55) 100%)",
            }}
          />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
          <div className="overline mb-3" style={{ color: "#d4ae6a" }}>CORPORATE FLEET SOLUTIONS</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium max-w-3xl leading-[1.05]" style={{ color: "#ffffff", textShadow: "0 2px 18px rgba(0,0,0,0.5)" }}>{lang === "ar" ? "حلول الأساطيل للشركات" : "Fleet solutions, built for enterprises"}</h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg leading-relaxed" style={{ color: "#e8e1cf" }}>{lang === "ar" ? "بوابة مخصصة للشركات الصغيرة والكبيرة لإدارة أسطولها." : "A dedicated portal for SMBs and enterprises to run their fleet operations."}</p>
        </div>
      </section>
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {services.map(([title, desc]) => (
          <div key={title} className="surface p-6">
            <ShieldCheck className="w-5 h-5 text-[#8a6f2e]" />
            <div className="font-display text-xl mt-3">{title}</div>
            <p className="text-sm text-[#525252] mt-1.5">{desc}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
